// Plain-text and Markdown import (VISION.md §3 lists both as reading formats).
// We wrap the text in a minimal valid EPUB 3 so the rest of the reader — TTS,
// translation, highlights, the whole notebook — works on it unchanged. No new
// reader path, just a converter feeding importEpub().

import JSZip from "jszip";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Very small inline-markdown renderer: **bold**, *italic* / _italic_, `code`.
 *  Escapes HTML first so raw text is always safe. */
function inline(md: string): string {
  let s = esc(md);
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  s = s.replace(/_([^_\n]+)_/g, "<em>$1</em>");
  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  return s;
}

interface Chapter {
  title: string;
  html: string;
}

/** Turn markdown/plain text into chapters of xhtml body content. A markdown
 *  H1 (`# `) starts a new chapter; plain text becomes one chapter (chunked if
 *  very long so pages stay responsive). */
function toChapters(text: string, isMarkdown: boolean, fallbackTitle: string): Chapter[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const chapters: Chapter[] = [];
  let curTitle = fallbackTitle;
  let buf: string[] = [];
  let list: string[] | null = null;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      buf.push(`<p>${inline(para.join(" ")).trim()}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list && list.length) {
      buf.push(`<ul>${list.map((li) => `<li>${inline(li)}</li>`).join("")}</ul>`);
    }
    list = null;
  };
  const pushChapter = () => {
    flushPara();
    flushList();
    if (buf.length) chapters.push({ title: curTitle, html: buf.join("\n") });
    buf = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (isMarkdown) {
      const h = line.match(/^(#{1,6})\s+(.*)$/);
      if (h) {
        const level = h[1].length;
        const title = h[2].trim();
        if (level === 1) {
          pushChapter();
          curTitle = title || curTitle;
          buf.push(`<h1>${esc(title)}</h1>`);
        } else {
          flushPara();
          flushList();
          buf.push(`<h${level}>${esc(title)}</h${level}>`);
        }
        continue;
      }
      if (/^\s*[-*]\s+/.test(line)) {
        flushPara();
        (list ??= []).push(line.replace(/^\s*[-*]\s+/, ""));
        continue;
      }
      if (/^\s*>\s?/.test(line)) {
        flushPara();
        flushList();
        buf.push(`<blockquote><p>${inline(line.replace(/^\s*>\s?/, ""))}</p></blockquote>`);
        continue;
      }
    }
    if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  pushChapter();

  // Plain text with no chapters → chunk long single chapter into ~120-paragraph
  // pieces so navigation and rendering stay snappy.
  if (chapters.length === 1 && !isMarkdown) {
    const paras = chapters[0].html.split("\n").filter(Boolean);
    if (paras.length > 160) {
      const out: Chapter[] = [];
      for (let i = 0; i < paras.length; i += 120) {
        out.push({
          title: `Part ${out.length + 1}`,
          html: paras.slice(i, i + 120).join("\n"),
        });
      }
      return out;
    }
  }
  return chapters.length ? chapters : [{ title: fallbackTitle, html: "<p></p>" }];
}

const CONTAINER_XML = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles><rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/></rootfiles>
</container>`;

const STYLES = `body{font-family:serif;line-height:1.6;margin:1em}h1,h2,h3{line-height:1.25}blockquote{border-left:3px solid #ccc;padding-left:1em;color:#555}code{font-family:monospace}`;

function chapterDoc(title: string, html: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="en">
<head><title>${esc(title)}</title><link rel="stylesheet" type="text/css" href="styles.css"/></head>
<body>${html}</body></html>`;
}

/** Convert a .txt or .md File into an EPUB File. */
export async function convertTextToEpub(file: File): Promise<File> {
  const raw = await file.text();
  const isMd = /\.(md|markdown)$/i.test(file.name);
  const baseTitle = file.name.replace(/\.(md|markdown|txt|text)$/i, "");
  // A leading markdown H1 becomes the book title.
  const h1 = isMd ? raw.match(/^\s*#\s+(.+)$/m)?.[1]?.trim() : undefined;
  const title = h1 || baseTitle || "Imported text";
  const chapters = toChapters(raw, isMd, title);

  const zip = new JSZip();
  zip.file("mimetype", "application/epub+zip", { compression: "STORE" });
  zip.file("META-INF/container.xml", CONTAINER_XML);
  zip.file("OEBPS/styles.css", STYLES);

  const manifest: string[] = [];
  const spine: string[] = [];
  const nav: string[] = [];
  chapters.forEach((ch, i) => {
    const id = `ch${i + 1}`;
    const href = `${id}.xhtml`;
    zip.file(`OEBPS/${href}`, chapterDoc(ch.title, ch.html));
    manifest.push(`<item id="${id}" href="${href}" media-type="application/xhtml+xml"/>`);
    spine.push(`<itemref idref="${id}"/>`);
    nav.push(`<li><a href="${href}">${esc(ch.title)}</a></li>`);
  });

  zip.file(
    "OEBPS/nav.xhtml",
    `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="en">
<head><title>${esc(title)}</title></head>
<body><nav epub:type="toc" id="toc"><h1>Contents</h1><ol>${nav.join("")}</ol></nav></body></html>`,
  );

  const uid = `urn:pagevoice:${Date.now()}-${Math.random().toString(36).slice(2)}`;
  zip.file(
    "OEBPS/content.opf",
    `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="uid">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="uid">${uid}</dc:identifier>
    <dc:title>${esc(title)}</dc:title>
    <dc:language>en</dc:language>
    <meta property="dcterms:modified">${new Date().toISOString().replace(/\.\d+Z$/, "Z")}</meta>
  </metadata>
  <manifest>
    <item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>
    <item id="css" href="styles.css" media-type="text/css"/>
    ${manifest.join("\n    ")}
  </manifest>
  <spine>${spine.join("")}</spine>
</package>`,
  );

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/epub+zip",
    compression: "DEFLATE",
  });
  return new File([blob], `${baseTitle}.epub`, { type: "application/epub+zip" });
}
